import React from "react";
import api from "../../Auth/Api";
import EventBus from "../../EventBus";
import { Button, Typography, Box } from "@mui/material";
import { PageContainer, SectionHeader, FormDialog, formatDate, preWrap } from "../../Layout/Common";
import PagedTable from "../../Layout/PagedTable";
import { subscribePoll } from "../../Layout/Poller";
import { AttachableTextField, AttachmentList, stripAttachmentLinks, uploadAll, disposePending } from "../../Layout/Attachments";
import DecisionDetailsDialog from "./DecisionDetailsDialog";

const PAGE = 10;
// One dialog serves Approve / Reject / Review: what differs is the label, whether the note is required and the action called.
const ACTS = {
    approve: { title: "Approve decision", confirm: "Approve", desc: "Approving", parts: ["answer"], noteRequired: false, label: "Note to the author (optional)" },
    reject:  { title: "Reject decision",  confirm: "Reject",  desc: "Rejecting", parts: ["answer"], noteRequired: true,  label: "Why it is rejected (required — sent to the author)" },
    review:  { title: "Review — send back for changes", confirm: "Send Review", desc: "Sending review", parts: ["review"], noteRequired: true, label: "Your feedback (sent to the author; decision marked needs-changes)" },
};

// Human-in-the-loop review:
//  - questions escalated by agents (`aidev hitl …` -> decision-request messages) with a reply flow;
//  - proposed decisions awaiting approval (Approve / Reject / Review = send back with feedback), each with a Details view.
// Both lists are server-paged and filtered server-side (messages {to:human, kind:decision-request, pending}).
export default class HitlPage extends React.Component {
    state = {
        requests: [], reqTotal: 0, reqPage: 1,
        decisions: [], decTotal: 0, decPage: 1,
        replyOpen: false, replyTo: null, replyText: "",
        act: null, actDec: null, actText: "",       // act = approve | reject | review
        details: null,
        pending: [], attachError: "", sending: false, progress: "",   // attachments of the open dialog (reply or decision action)
    };
    componentDidMount() { this.load(); this.unsub = subscribePoll(this.load, 10000); }
    componentWillUnmount() { if (this.unsub) this.unsub(); disposePending(this.state.pending); }
    // Coalescing guard: a load requested while one runs is re-run once it ends (never dropped, never overlapped).
    load = async () => {
        if (this.loading) { this.pending = true; return true; }
        this.loading = true;
        try {
            const { reqPage, decPage } = this.state;
            const a = api.development("Loading review queue", ["messages"], { silent: true,
                payload: { to: "human", kind: "decision-request", pending: true, skip: (reqPage - 1) * PAGE, limit: PAGE } })
                .then(r => { if (r && r.messages) this.setState({ requests: r.messages, reqTotal: r.total || 0 }); });
            const b = api.development("Loading decisions", ["decisions"], { silent: true,
                payload: { status: "proposed", skip: (decPage - 1) * PAGE, limit: PAGE } })
                .then(r => { if (r && r.decisions) this.setState({ decisions: r.decisions, decTotal: r.total || 0 }); });
            const results = await Promise.allSettled([a, b]);
            return results.every(x => x.status === "fulfilled");
        } finally {
            this.loading = false;
            if (this.pending) { this.pending = false; this.load(); }
        }
    };
    setReqPage = (p) => this.setState({ reqPage: p }, this.load);
    setDecPage = (p) => this.setState({ decPage: p }, this.load);
    // Every dialog shares the attachment flow: upload (compressing if needed), then post with the file list.
    submit = async (desc, parts, payload, done) => {
        if (this.state.sending) return;
        this.setState({ sending: true, attachError: "", progress: "" });
        try {
            const attachments = this.state.pending.length ? await uploadAll(this.state.pending, p => this.setState({ progress: p })) : [];
            await api.development(desc, parts, { payload: { ...payload, attachments } });
            disposePending(this.state.pending);
            this.setState({ ...done, pending: [] }); this.load();
            EventBus.emit("aidev-inbox");
        } catch (e) { this.setState({ attachError: e.message || String(e) }); }
        finally { this.setState({ sending: false, progress: "" }); }
    };
    // Opening a composer drops the previous dialog's pending files.
    resetPending = () => { disposePending(this.state.pending); return { pending: [], attachError: "" }; };
    openReply = (m) => this.setState({ replyOpen: true, replyTo: m, replyText: "", ...this.resetPending() });
    closeReply = () => { if (!this.state.sending) this.setState({ replyOpen: false, ...this.resetPending() }); };
    sendReply = () => {
        const { replyTo, replyText } = this.state;
        if (!replyTo || !replyText.trim()) return;
        this.submit("Replying", ["post-message"], { to: replyTo.from, kind: "note", body: replyText, threadId: replyTo.id }, { replyOpen: false, replyTo: null, replyText: "" });
    };
    openAct = (d, act) => this.setState({ act, actDec: d, actText: "", details: null, ...this.resetPending() });
    closeAct = () => { if (!this.state.sending) this.setState({ act: null, actDec: null, ...this.resetPending() }); };
    sendAct = () => {
        const { act, actDec, actText } = this.state;
        const spec = act && ACTS[act]; if (!spec || !actDec) return;
        const note = actText.trim();
        if (spec.noteRequired && !note) return;
        const payload = act === "review" ? { id: actDec.id, note } : { id: actDec.id, approved: act === "approve", note: note || undefined };
        this.submit(spec.desc, spec.parts, payload, { act: null, actDec: null, actText: "" });
    };
    render() {
        const { requests, reqTotal, reqPage, decisions, decTotal, decPage, replyOpen, replyTo, replyText,
                act, actDec, actText, details, pending, attachError, sending, progress } = this.state;
        const spec = act ? ACTS[act] : null;
        const attachProps = { pending, onPending: p => this.setState({ pending: p }), error: attachError, onError: err => this.setState({ attachError: err }) };
        const body = (m) => <><Typography component="span" variant="body2" sx={preWrap}>{stripAttachmentLinks(m.body, m.attachments)}</Typography><AttachmentList attachments={m.attachments} /></>;
        return (
            <PageContainer maxWidth={1000}>
                <SectionHeader title="Human Review" />

                <Typography variant="h6" sx={{ mb: 1 }}>Questions from agents</Typography>
                <PagedTable
                    columns={[
                        { id: "createdDate", label: "Date", sx: { whiteSpace: "nowrap" }, render: m => formatDate(m.createdDate) },
                        { id: "from", label: "Agent" },
                        { id: "body", label: "Question", width: "60%", render: body },
                        { id: "actions", label: "", width: "1%", render: m => <Button size="small" variant="contained" disabled={sending} onClick={() => this.openReply(m)}>Reply</Button> },
                    ]}
                    rows={requests} total={reqTotal} page={reqPage} pageSize={PAGE} onPage={this.setReqPage}
                    getRowKey={m => m.id} sx={{ mb: 1 }}
                />

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Proposed decisions</Typography>
                <PagedTable
                    columns={[
                        { id: "createdDate", label: "Date", sx: { whiteSpace: "nowrap" }, render: d => formatDate(d.createdDate) },
                        { id: "author", label: "Author" },
                        { id: "kind", label: "Kind" },
                        { id: "summary", label: "Summary", width: "45%", sx: preWrap },
                        {
                            id: "actions", label: "Actions", width: "1%", sx: { whiteSpace: "nowrap" },
                            render: (d) => (
                                <>
                                    <Button size="small" disabled={sending} onClick={() => this.setState({ details: d })}>Details</Button>
                                    <Button color="primary" size="small" disabled={sending} onClick={() => this.openAct(d, "approve")} sx={{ ml: 1 }}>Approve</Button>
                                    <Button color="error" size="small" disabled={sending} onClick={() => this.openAct(d, "reject")} sx={{ ml: 1 }}>Reject</Button>
                                    <Button size="small" disabled={sending} onClick={() => this.openAct(d, "review")} sx={{ ml: 1 }}>Review</Button>
                                </>
                            )
                        },
                    ]}
                    rows={decisions} total={decTotal} page={decPage} pageSize={PAGE} onPage={this.setDecPage}
                    getRowKey={d => d.id}
                />

                <FormDialog open={replyOpen} title={`Reply to ${replyTo?.from}`} onClose={this.closeReply}
                    onConfirm={this.sendReply} confirmText={sending ? (progress || "Sending…") : "Send"} confirmDisabled={!replyText.trim() || sending} cancelDisabled={sending} maxWidth="sm" fullWidth>
                    {replyTo && <Box sx={{ mb: 1, color: "text.secondary" }}>{body(replyTo)}</Box>}
                    <AttachableTextField label="Your answer (paste screenshots here)" autoFocus value={replyText} onChange={v => this.setState({ replyText: v })} {...attachProps} />
                </FormDialog>

                {/* Approve / Reject / Review share this dialog (note + attachments go to the author as a note). */}
                <FormDialog open={!!act} title={spec ? spec.title : ""} onClose={this.closeAct}
                    onConfirm={this.sendAct} confirmText={sending ? (progress || "Sending…") : (spec ? spec.confirm : "")}
                    confirmDisabled={sending || (spec && spec.noteRequired && !actText.trim())} cancelDisabled={sending} maxWidth="sm" fullWidth>
                    {actDec && <>
                        <Typography variant="body2" sx={{ ...preWrap, mb: 0.5 }}><strong>{actDec.author}</strong> ({actDec.kind}): {actDec.summary}</Typography>
                        {actDec.rationale && <Typography variant="caption" sx={{ ...preWrap, display: "block", color: "text.secondary", mb: 1 }}>{actDec.rationale}</Typography>}
                    </>}
                    <AttachableTextField label={spec ? spec.label : ""} autoFocus value={actText} onChange={v => this.setState({ actText: v })} {...attachProps} />
                </FormDialog>

                <DecisionDetailsDialog decision={details} onClose={() => this.setState({ details: null })}>
                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                        <Button size="small" variant="contained" color="primary" disabled={sending} onClick={() => this.openAct(details, "approve")}>Approve</Button>
                        <Button size="small" variant="outlined" color="error" disabled={sending} onClick={() => this.openAct(details, "reject")}>Reject</Button>
                        <Button size="small" variant="outlined" disabled={sending} onClick={() => this.openAct(details, "review")}>Review</Button>
                    </Box>
                </DecisionDetailsDialog>
            </PageContainer>
        );
    }
}
